import type {
	Application, Block, Message, MessageWithContents, Session
} from '@types'

import axios from 'axios'

import type { UserApplication } from 'libs/types/src/user-application'

import env from '../environments'

export async function startApplication(id: string, brainIds?: string[], contentIds?: string[], prompt?: string) {
	const response = await axios.post(`${env.apiUrl}/applications/${id}`, {
		contentIds,
		brainIds,
		prompt
	})

	return response.data as {messages: Message[]; session: Session}
}

export async function getAllApplications() {
	const response = await axios.get(`${env.apiUrl}/applications`)

	return response.data as Application[]
}

export async function createApplication() {
	const response = await axios.post(`${env.apiUrl}/applications/create`)

	return (response.data as {application: Application}).application
}

export async function duplicateApplication(id: string) {
	const response = await axios.post(`${env.apiUrl}/applications/${id}/duplicate`)

	return (response.data as {application: Application}).application
}

export async function sendFollowUpQuestion(blockId: string, sessionId: string, followup: string, blockContent: string, isReasoning: boolean) {
	const endpoint = `${env.apiUrl}/applications/session/${blockId}/followup`

	const payload = {
		followup,
		sessionId,
		blockContent,
		isReasoning
	}

	const response = await axios.post(endpoint, payload)

	return (response.data as {message: Message}).message
}

/* -------------------------------------------------- UPDATES -------------------------------------------------- */

export async function updateApplication(id: string, application: Partial<Application>) {
	await axios.put(`${env.apiUrl}/applications/${id}`, { application })
}

export async function updateSessionTitle(sessionId: string, title: string) {
	await axios.put(`${env.apiUrl}/applications/session/${sessionId}`, { title })
}

/* -------------------------------------------------- GETS -------------------------------------------------- */

export async function getSessionMessages(id: string) {
	const response = await axios.get(`${env.apiUrl}/applications/session/${id}/messages`)

	return response.data as MessageWithContents[]
}

export async function getSessions(id: string) {
	const response = await axios.get(`${env.apiUrl}/applications/${id}/sessions`)

	return (response.data as {sessions: Session[]}).sessions
}

export async function getSession(sessionId: string) {
	const response = await axios.get(`${env.apiUrl}/applications/sessions/${sessionId}`)

	return (response.data as {session: Session}).session
}

export async function getApplicationBlocks(id: string) {
	const response = await axios.get(`${env.apiUrl}/applications/${id}/blocks`)

	return (response.data as {blocks: Block[]}).blocks
}

export async function getBlocksBrains(blockIds: string[]) {
	const response = await axios.get(`${env.apiUrl}/applications/blocksBrains`, {
		params: { blockIds }
	})

	return (response.data as {blocksBrains: Array<{brainIds: string[]; blockId: string}>}).blocksBrains
}

export async function getApplicationUsers(id: string) {
	const response = await axios.get(`${env.apiUrl}/applications/sharedWith/${id}`)

	return response.data as UserApplication[]
}

/* -------------------------------------------------- DELETES -------------------------------------------------- */

export async function deleteApplication(id: string) {
	await axios.delete(`${env.apiUrl}/applications/${id}`)
}

export async function deleteSession(sessionId: string) {
	await axios.delete(`${env.apiUrl}/applications/session/${sessionId}`)
}
